import powerBiReportsService from '@/services/powerBiReports'

// El id del timer vive fuera del state por la misma razón que fetchPromise en
// powerBiReports/actions.js: no aporta nada dentro del Proxy reactivo de Pinia
// y solo dispara renders. Hay un solo polling vivo a la vez.
let pollTimerId = null

// Generación del ciclo de polling. Una respuesta que estaba en vuelo cuando el
// usuario cerró el diálogo o pidió otra descarga no debe escribir el state:
// mismo cuidado que embedAtSchedule en PowerBiReportDashboard.
let pollGeneration = 0

// Generación de arranque. Sin esto, dos startExport solapados (doble click,
// "generar otra" repetido antes de que el primero resuelva) pueden resolver
// en cualquier orden: la respuesta del más viejo llegaría después y pisaría
// el jobId/status del más nuevo con datos de un job que ya no es el vigente.
let startGeneration = 0

// setTimeout recursivo, no setInterval: así el siguiente sondeo se agenda
// cuando el anterior terminó y no se encima si la red va lenta.
const POLL_INTERVAL_MS = 3000

const UNEXPECTED_ERROR = 'UNEXPECTED_ERROR'

function clearPollTimer () {
  if (pollTimerId) {
    clearTimeout(pollTimerId)
    pollTimerId = null
  }
}

// responser.js rechaza con el AxiosResponse crudo, así que el código del
// backend está en error.data y el HTTP en error.status — no en error.response.
function readErrorCode (error) {
  return error?.data?.errorCode ?? UNEXPECTED_ERROR
}

// Compartida por pollStatus y refreshStatusOnce para no duplicar la lectura
// de la respuesta en los dos lugares que la consumen.
function applyStatus (store, data) {
  store.status = data.status
  store.percentComplete = data.percentComplete ?? 0
  store.downloadUrl = data.downloadUrl ?? null
  store.errorCode = data.errorCode ?? null
}

export default {
  async startExport ({ reportId, reportName = '', format, bookmarkState = null }) {
    const generation = ++startGeneration

    this.cancelPolling()
    this.$reset()
    this.isStarting = true

    try {
      const { data } = await powerBiReportsService.startExport(reportId, { format, bookmarkState })

      // Otro startExport arrancó mientras este esperaba respuesta: ese ya es
      // el vigente, esta respuesta llegó tarde y no le corresponde a nadie.
      if (generation !== startGeneration) return false

      this.jobId = data.exportJobId
      this.reportId = reportId
      this.reportName = reportName
      this.format = format
      this.status = data.status
      this.pollStatus()

      return true
    } catch (error) {
      console.error(error)
      if (generation !== startGeneration) return false

      this.status = 'failed'
      this.errorCode = readErrorCode(error)
      this.retryAfterSeconds = error?.data?.retryAfterSeconds ?? null

      return false
    } finally {
      if (generation === startGeneration) this.isStarting = false
    }
  },

  pollStatus () {
    const generation = ++pollGeneration

    const tick = async () => {
      if (generation !== pollGeneration) return

      try {
        const { data } = await powerBiReportsService.getExportStatus(this.jobId)

        // El await de arriba pudo tardar: si mientras tanto se canceló o se
        // empezó otra descarga, esta respuesta ya no le corresponde a nadie.
        if (generation !== pollGeneration) return

        applyStatus(this, data)

        if (data.status === 'succeeded' || data.status === 'failed') {
          clearPollTimer()

          return
        }

        pollTimerId = setTimeout(tick, POLL_INTERVAL_MS)
      } catch (error) {
        console.error(error)
        if (generation !== pollGeneration) return

        this.status = 'failed'
        this.errorCode = readErrorCode(error)
        clearPollTimer()
      }
    }

    // Primer sondeo con retraso: el export tarda decenas de segundos, así que
    // preguntar de inmediato solo gasta un request.
    pollTimerId = setTimeout(tick, POLL_INTERVAL_MS)
  },

  // Consulta suelta, sin agendar timer ni tocar pollGeneration: sirve para
  // refrescar el estado en un punto puntual (por ejemplo al reabrir un
  // dialogo que dejo de sondear) antes de decidir si hace falta retomar
  // pollStatus o si el job ya llego a un estado terminal mientras tanto.
  async refreshStatusOnce () {
    if (!this.jobId) return

    try {
      const { data } = await powerBiReportsService.getExportStatus(this.jobId)

      applyStatus(this, data)
    } catch (error) {
      console.error(error)
      this.status = 'failed'
      this.errorCode = readErrorCode(error)
    }
  },

  // Obligatorio en onBeforeUnmount, al cerrar el diálogo y al cambiar de
  // reporte. No cancela el trabajo del backend — ese sigue y termina
  // entregando por la campanita, que es justo el comportamiento buscado.
  cancelPolling () {
    pollGeneration += 1
    clearPollTimer()
  },

  async downloadExportFile () {
    if (!this.downloadUrl) return null

    const { data, headers } = await powerBiReportsService.downloadExportFile(this.downloadUrl)

    return { data, headers }
  },

  // Best effort a propósito: si el acuse no llega, el backend manda la
  // notificación y el usuario recibe el archivo igual. Una campanita de más es
  // mucho mejor que un error por algo que ya salió bien.
  async acknowledgeDownload () {
    if (!this.jobId) return

    try {
      await powerBiReportsService.ackExportDownload(this.jobId)
    } catch (error) {
      console.error(error)
    }
  },

  // Equivalente de acknowledgeDownload para el camino de error: si el diálogo
  // seguía abierto cuando el polling trajo el fallo, el usuario ya vio el
  // toast negativo en vivo, y este acuse es lo que le dice al backend que no
  // hace falta la notificación diferida. Best effort por la misma razón.
  async acknowledgeFailure () {
    if (!this.jobId) return

    try {
      await powerBiReportsService.ackExportFailure(this.jobId)
    } catch (error) {
      console.error(error)
    }
  },

  reset () {
    this.cancelPolling()
    this.$reset()
  }
}
